import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import skillSheetApi from "@/api/skillSheet";

export default {

  /**
   * 日付文字列を「YYYY年MM月DD日」形式に変換
   * @param {string} dateString - 日付文字列
   * @returns {string} - 整形された日付文字列
   */
  formatDate(dateString) {
    if (!dateString) {
      return "";
    }

    const date = new Date(dateString);

    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');

    return `${year}年${month}月`;
  },

  /**
   * 指定文字数でテキストを分割
   * @param {string} text - 対象テキスト
   * @param {number} maxChars - 分割する文字数
   * @returns {string[]} - 分割された文字列配列
   */
  splitByLength(text = "", maxWeightedLength = 30, propertyType = "businessContent") {
    function charWeight(char, propertyType) {
      switch (propertyType) {
        case 'businessContent':
          if (char === ' ') return 0.5;
          if (char.match(/[A-Z]/)) return 30 / 40;
          if (char.match(/[a-z]/)) return 30 / 47;
          if (char.match(/[0-9]/)) return 30 / 57;
          if (char.match(/[\u3040-\u30FF]/)) return 30 / 33;
          if (char.match(/[\u4E00-\u9FFF]/)) return 1;
          return 1;
        case 'developmentEnvironment':
          if (char === ' ') return 0.5;
          if (char.match(/[A-Z]/)) return 10 / 13;
          if (char.match(/[a-z]/)) return 10 / 16;
          if (char.match(/[0-9]/)) return 10 / 19;
          if (char.match(/[\u3040-\u30FF]/)) return 1;
          if (char.match(/[\u4E00-\u9FFF]/)) return 1;
          return 1;
        case 'role':
          if (char === ' ') return 0.5;
          if (char.match(/[A-Z]/)) return 8 / 10;
          if (char.match(/[a-z]/)) return 8 / 12;
          if (char.match(/[0-9]/)) return 8 / 15;
          if (char.match(/[\u3040-\u30FF]/)) return 1;
          if (char.match(/[\u4E00-\u9FFF]/)) return 1;
          return 1;
        default:
          return 1;
      }
    }

    let result = [];
    let currentLine = "";
    let currentWeight = 0;

    for (let i = 0; i < text.length; i++) {
      const char = text[i];
      const w = charWeight(char, propertyType);

      if (currentWeight + w > maxWeightedLength) {

        result.push(currentLine);
        currentLine = char;
        currentWeight = w;
      } else {
        currentLine += char;
        currentWeight += w;
      }
    }

    if (currentLine) {
      result.push(currentLine);
    }

    return result;
  },

  /**
   * センチメートルからインチへ変換
   * @param {number} cm - センチメートル
   * @returns {number} - インチ
   */
  cmToInch(cm) {
    return cm / 2.54;
  },


  /**
* テキストを最大136文字で分割し、1行ずつExcelセルに書き込む
* @param {any} worksheet - 対象のワークシート
* @param {string} text - 書き込む文字列
* @param {number} rowIndexRef - 現在の行番号（参照型で渡す）
* @param {string} column - 書き込み対象列（例: 'A'）
* @param {number} max_rows - 最大の行数
* @param {number} chunk_size - 1行に入る最大文字数
*/

  writeTextToCell(worksheet, text, rowIndexRef, column, chunk_size) {
    if (!text) return;

    let start = 0;
    let length = 0;
    let weightSum = 0;

    function charWeight(char) {
      if (char === ' ') return 0.5;
      if (char.match(/[A-Z]/)) return 45 / 58;
      if (char.match(/[a-z]/)) return 45 / 70;
      if (char.match(/[0-9]/)) return 45 / 84;
      if (char.match(/[\u3040-\u30FF]/)) return 45 / 47;
      if (char.match(/[\u4E00-\u9FFF]/)) return 1;
      return 1;
    }
    while (start < text.length) {
      weightSum = 0;
      length = 0;

      for (let i = start; i < text.length; i++) {
        weightSum += charWeight(text[i]);
        if (weightSum > chunk_size) break;
        length++;
      }

      let chunk = text.substr(start, length)
      const cell = worksheet.getCell(`${column}${rowIndexRef.value}`);
      cell.value = chunk;
      cell.font = { name: 'ＭＳ Ｐゴシック', bold: false, size: 10 };
      cell.alignment = { vertical: 'middle' };

      rowIndexRef.value++;
      start += length;
    }
  },

  /**
   * Excelファイルを作成
   * @param {array} skillSheetData - スキルシートデータ
   * @param {string} staffName - 社員名
   */
  async createExcelFile(skillSheetData, staffName) {

    const workbook = new ExcelJS.Workbook();

    /**--------------------------------------------------------------------------------------------------------------------------
     * シート１「職務経歴書」
     * 
     *  */
    const worksheet1 = workbook.addWorksheet('職務経歴書', {
      views: [{ style: 'pageBreakPreview', showGridLines: false }],
      pageSetup: { paperSize: 9, orientation: 'portrait' }
    });

    worksheet1.pageSetup.margins = {
      left: this.cmToInch(1.8), right: this.cmToInch(1.8),
      top: this.cmToInch(1.9), bottom: this.cmToInch(1.9),
      header: 0, footer: 0
    };

    worksheet1.properties.defaultColWidth = 3.4;

    for (let col = 1; col <= 25; col++) {
      worksheet1.getColumn(col).width = 3.4;
    }

    for (let row = 1; row <= 145; row++) {
      worksheet1.getRow(row).height = 14.3;
    }

    const specialRows = [1, 2, 3, 4, 5, 6, 7, 8];
    specialRows.forEach(row => {
      worksheet1.getRow(row).height = 18;
    });

    worksheet1.mergeCells('A1:Y3');
    const titleCell = worksheet1.getCell('A1');
    titleCell.value = '職務経歴書';
    titleCell.font = { name: 'ＭＳ Ｐゴシック', bold: true, size: 15 };
    titleCell.alignment = { horizontal: 'center', vertical: 'middle' };
    titleCell.border = {
      top: { style: 'thin', color: { argb: '000000' } },
      left: { style: 'thin', color: { argb: '000000' } },
      right: { style: 'thin', color: { argb: '000000' } },
    };

    worksheet1.getCell('A5').value = '氏名：';
    worksheet1.getCell('N5').value = '最終学歴：';
    worksheet1.getCell('A6').value = '性別 /年齢：';
    worksheet1.getCell('N6').value = '最寄り駅：';

    ['A5', 'N5', 'A6', 'N6'].forEach(cell => {
      worksheet1.getCell(cell).alignment = { horizontal: 'left', vertical: 'bottom' };
      worksheet1.getCell(cell).font = { name: 'ＭＳ Ｐゴシック', bold: false, size: 10 };
    });

    const underlineCells = [
      ...'ABCDEFGHIJKL'.split('').map(col => `${col}5`),
      ...'NOPQRSTUVWXY'.split('').map(col => `${col}5`),
      ...'ABCDEFGHIJKL'.split('').map(col => `${col}6`),
      ...'NOPQRSTUVWXY'.split('').map(col => `${col}6`),
    ];

    underlineCells.forEach(cell => {
      let existingBorder = worksheet1.getCell(cell).border || {};
      worksheet1.getCell(cell).border = {
        ...existingBorder,
        bottom: { style: 'dotted', color: { argb: '000000' } },
      };
    });

    worksheet1.getCell('C5').value = skillSheetData?.careerData.staffName || '';
    worksheet1.getCell('Q5').value = skillSheetData?.careerData.academic || '';
    worksheet1.getCell('D6').value = `${skillSheetData.careerData.gender ? '　 ' + skillSheetData.careerData.gender + '  /  ' : ''}${skillSheetData.careerData.age}歳` || '';
    worksheet1.getCell('Q6').value = skillSheetData?.careerData.nearestStation || '';

    const fontTextCells = ['C5', 'Q5', 'D6', 'Q6'];
    fontTextCells.forEach(cell => {
      worksheet1.getCell(cell).font = { name: 'ＭＳ Ｐゴシック', bold: false, size: 10 };
    });

    let rowIndex1 = { value: 9 };
    const dynamicHeaderRows = [];

    function addHeader(title) {
      worksheet1.mergeCells(`A${rowIndex1.value}:Y${rowIndex1.value}`);
      const cell = worksheet1.getCell(`A${rowIndex1.value}`);
      cell.value = title;
      cell.font = { name: 'ＭＳ Ｐゴシック', bold: true, size: 10 };
      cell.alignment = { horizontal: 'center', vertical: 'middle' };
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'BFBFBF' } };
      worksheet1.getRow(rowIndex1.value).height = 18;
      dynamicHeaderRows.push(rowIndex1.value);
      rowIndex1.value++;
    }

    function addEmptyRows(worksheet, rowIndexRef, count, column = 'A') {
      for (let i = 0; i < count; i++) {
        const cell = worksheet.getCell(`${column}${rowIndexRef.value}`);
        cell.value = '';
        rowIndexRef.value++;
      }
    }

    const minBiographyRows = 7;
    const minPublicRelationsRows = 11;
    const minSpecialtyFieldRows = 3;
    const minQualificationRows = 9;

    // 略歴
    addHeader('略歴');
    const biography = skillSheetData?.careerData.biography.split('\n') || [];
    const startBiographyRow = rowIndex1.value;
    biography.forEach(item => {
      this.writeTextToCell(worksheet1, item, rowIndex1, 'A', 9999, 55);
    });
    const endBiographyRow = rowIndex1.value;
    const biographyLineCount = endBiographyRow - startBiographyRow;
    if (biographyLineCount < minBiographyRows) {
      addEmptyRows(worksheet1, rowIndex1, minBiographyRows - biographyLineCount, 'A');
    }
    addEmptyRows(worksheet1, rowIndex1, 1, 'A');

    // 自己PR
    addHeader('自己PR');
    const publicRelations = skillSheetData?.careerData.publicRelations?.split('\n') || [];
    const startPRRow = rowIndex1.value;
    publicRelations.forEach(item => {
      this.writeTextToCell(worksheet1, item, rowIndex1, 'A', 9999, 55);
    });
    const endPRRow = rowIndex1.value;
    const publicRelationsLineCount = endPRRow - startPRRow;
    if (publicRelationsLineCount < minPublicRelationsRows) {
      addEmptyRows(worksheet1, rowIndex1, minPublicRelationsRows - publicRelationsLineCount, 'A');
    }
    addEmptyRows(worksheet1, rowIndex1, 1, 'A');

    // 得意分野
    addHeader('得意分野');
    const specialtyField = skillSheetData?.careerData.specialtyField?.split('\n') || [];
    const startSpecialtyRow = rowIndex1.value;
    specialtyField.forEach(item => {
      this.writeTextToCell(worksheet1, item, rowIndex1, 'A', 9999, 55);
    });
    const endSpecialtyRow = rowIndex1.value;
    const specialtyFieldLineCount = endSpecialtyRow - startSpecialtyRow;
    if (specialtyFieldLineCount < minSpecialtyFieldRows) {
      addEmptyRows(worksheet1, rowIndex1, minSpecialtyFieldRows - specialtyFieldLineCount, 'A');
    }
    addEmptyRows(worksheet1, rowIndex1, 1, 'A');

    // 保有資格
    addHeader('保有資格');
    const qualification = skillSheetData?.careerData.qualification?.split('\n') || [];
    const startQualificationRow = rowIndex1.value;
    qualification.forEach(item => {
      this.writeTextToCell(worksheet1, item, rowIndex1, 'A', 9999, 55);
    });
    const endQualificationRow = rowIndex1.value;
    const qualificationLineCount = endQualificationRow - startQualificationRow;
    if (qualificationLineCount < minQualificationRows) {
      addEmptyRows(worksheet1, rowIndex1, minQualificationRows - qualificationLineCount, 'A');
    }
    addEmptyRows(worksheet1, rowIndex1, 1, 'A');

    const allHeaderRows = specialRows.concat(dynamicHeaderRows).filter(row => ![4, 5, 6, 7, 8].includes(row));
    allHeaderRows.forEach(row => {
      const leftCell = worksheet1.getCell(`A${row}`);
      const rightCell = worksheet1.getCell(`Y${row}`);

      leftCell.border = {
        ...leftCell.border,
        left: { style: 'thin', color: { argb: '000000' } },
        top: { style: 'thin', color: { argb: '000000' } },
      };

      rightCell.border = {
        ...rightCell.border,
        right: { style: 'thin', color: { argb: '000000' } },
        bottom: { style: 'thin', color: { argb: '000000' } },
      };
    });

    worksheet1.pageSetup.horizontalCentered = true;
    worksheet1.pageSetup.printArea = `A1:Y${rowIndex1.value - 1}`;


    /**------------------------------------------------------------------------------------------------------------------------------
     * シート２ 「職務経歴書」
     * 
     */
    const worksheet2 = workbook.addWorksheet('業務関連', {
      views: [
        {
          style: 'pageBreakPreview',
          showGridLines: false // 目盛線を削除
        }
      ],
      pageSetup: { paperSize: 9, orientation: 'portrait' }
    });

    worksheet2.pageSetup.margins = {
      left: this.cmToInch(1.3), right: this.cmToInch(1.3),
      top: this.cmToInch(1.4), bottom: this.cmToInch(1.4),
      header: 0, footer: 0
    };
    // worksheet2.pageSetup.printTitlesRow = '5';

    // 各列の幅を固定 (A列を2.78に設定)
    worksheet2.getColumn(1).width = 3.6;

    // 各列の幅を固定 (B~E列を3.22に設定)
    for (let col = 2; col <= 5; col++) {
      worksheet2.getColumn(col).width = 4.00;
    }
    // 各列の幅を固定 (F~Q列を4.11に設定)
    for (let col = 6; col <= 17; col++) {
      worksheet2.getColumn(col).width = 4.9;
    }

    // 各列の幅を固定 (R~U列を4.22に設定)
    for (let col = 18; col <= 21; col++) {
      worksheet2.getColumn(col).width = 5.00;
    }

    // 各列の幅を固定 (V~Y列を3.22に設定)
    for (let col = 22; col <= 26; col++) {
      worksheet2.getColumn(col).width = 4.00;
    }


    // セルの結合設定
    const mergedCells2 = ['A5', 'B5:E5', 'F5:Q5', 'R5:U5', 'V5:Y5'];

    mergedCells2.forEach(range => {
      worksheet2.mergeCells(range);

      // 結合セルの最初のセルを取得しスタイルを適用
      const cell = worksheet2.getCell(range.split(':')[0]);

      // 上揃え & 自動折り返し
      cell.alignment = { horizontal: 'center', vertical: 'top', };

      cell.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'BFBFBF' },
      };

      // 実線（境界線）を維持（上、下、左、右すべて設定）
      cell.border = {
        top: { style: 'thin', color: { argb: '000000' } },
        left: { style: 'thin', color: { argb: '000000' } },
        right: { style: 'thin', color: { argb: '000000' } },
        bottom: { style: 'thin', color: { argb: '000000' } }
      };
    });

    // 全体のデフォルトフォントを設定 (デフォルト 10pt)
    worksheet2.eachRow((row) => {
      row.eachCell((cell) => {
        cell.font = { name: 'ＭＳ Ｐゴシック', size: 10 };
      });
    });

    // 3. タイトル "職務経歴書" を結合し、スタイルを適用
    worksheet2.mergeCells('A1:Y4'); // A1からD1まで結合
    const titleCell2 = worksheet2.getCell('A1');
    titleCell2.value = '職務経歴書';
    titleCell2.font = { name: 'ＭＳ Ｐゴシック', bold: true, size: 15 } // 文字サイズおよび太字適用
    titleCell2.alignment = { horizontal: 'center', vertical: 'middle' }; // 中央揃え
    titleCell2.border = {
      top: { style: 'thin' },
      left: { style: 'thin' },
      right: { style: 'thin' },
      bottom: undefined
    };

    // ヘッダーに入る値の配列
    const headerTitles = ['開発期間', '業務内容', '開発環境等', '役割'];

    // 結合セルの開始位置
    const headerPositions = ['B5', 'F5', 'R5', 'V5'];

    headerPositions.forEach((position, index) => {
      const cell = worksheet2.getCell(position);
      cell.value = headerTitles[index];

      // 中央揃え
      cell.alignment = { horizontal: 'center', vertical: 'middle' };

      // フォントスタイルを適用
      cell.font = { name: 'ＭＳ Ｐゴシック', bold: false, size: 10 };

      if (!cell.font.name || cell.font.name !== 'ＭＳ Ｐゴシック') {
        cell.font = { name: 'MS PGothic', family: 1, bold: false, size: 10 };
      }

      // 境界線を適用
      cell.border = {
        top: { style: 'thin', color: { argb: '000000' } },
        left: { style: 'thin', color: { argb: '000000' } },
        right: { style: 'thin', color: { argb: '000000' } },
        bottom: { style: 'thin', color: { argb: '000000' } }
      };
    });

    let rowIndex = 6; // データ開始行


    skillSheetData?.careerItemsData.forEach((item, index) => {
      let businessLines = item.businessContent
        .split('\n')
        .flatMap(line => line === '' ? [''] : this.splitByLength(line, 30, 'businessContent'));

      let environmentLines = item.developmentEnvironment
        .split('\n')
        .flatMap(line => line === '' ? [''] : this.splitByLength(line, 10, 'developmentEnvironment'));

      let roleLines = item.role
        .split('\n')
        .flatMap(line => line === '' ? [''] : this.splitByLength(line, 8, 'role'));

      // developmentDateを改行(\n)なしで配列に変換
      let developmentDateLines = [
        item.developmentStartDate ? this.formatDate(item.developmentStartDate) : null,
        '～',
        this.formatDate(item.developmentEndDate),
        `（${item.developmentPeriod}）`
      ].filter(Boolean); // 空の値を削除

      // 行数を最も長いデータに基づいて設定
      const maxRows = Math.max(
        developmentDateLines.length,
        businessLines.length,
        environmentLines.length,
        roleLines.length
      );

      const middleRow = Math.floor(maxRows / 2); // A列（番号）を入力する中央行を計算

      for (let i = 0; i < maxRows; i++) {
        const row = worksheet2.getRow(rowIndex);
        const isMiddleRow = i === middleRow;  // 中央行かどうか
        const isLastRow = i === maxRows - 1; // 最終行かどうか

        // 中央行にのみ A列（番号）を入力
        if (isMiddleRow) {
          row.getCell(1).value = index + 1; // A列
        }

        // 結合セル（B列）に値を設定
        const devDateText = developmentDateLines[i] || '';
        const cellB = row.getCell(2);
        cellB.value = devDateText;
        // データがある場合のみ B~E列を結合して値を代入
        if (devDateText !== '') {
          worksheet2.mergeCells(`B${rowIndex}:E${rowIndex}`);
          const cellB = row.getCell(2);
          cellB.value = devDateText;

          // "（xxヶ月）"なら中央揃え、それ以外は左揃え
          cellB.alignment = devDateText.includes('（') && devDateText.includes('）')
            ? { wrapText: false, vertical: 'middle', horizontal: 'center' }
            : { wrapText: false, vertical: 'middle', horizontal: 'left' };

          cellB.font = { name: 'ＭＳ Ｐゴシック', bold: false, size: 10 };
        }

        // 既存のデータ入力方式を維持
        row.getCell(6).value = businessLines[i] || ''; // F列
        row.getCell(18).value = environmentLines[i] || ''; // R列
        row.getCell(22).value = roleLines[i] || ''; // V列

        // `forEach` を使用してフォントスタイルを一括適用
        [6, 18, 22].forEach(col => {
          let cell = row.getCell(col);
          cell.alignment = { wrapText: false, vertical: 'middle', horizontal: 'left' };
          cell.font = { name: 'ＭＳ Ｐゴシック', bold: false, size: 10 };

          // フォントが適用されていない場合は `MS PGothic` を代替として使用
          if (!cell.font.name || cell.font.name !== 'ＭＳ Ｐゴシック') {
            cell.font = { name: 'MS PGothic', family: 1, bold: false, size: 10 };
          }
        });
        let cell = row.getCell(1);
        cell.alignment = { wrapText: false, vertical: 'middle', horizontal: 'center' };
        cell.font = { name: 'ＭＳ Ｐゴシック', bold: false, size: 10 };

        // 外枠のみ適用（内部罫線を削除）
        for (let col = 1; col <= 25; col++) {
          let cell = row.getCell(col);
          let borderStyle = {};

          if (i === 0) { // 最初の行 → 上、左、右の罫線を適用
            borderStyle = {
              top: { style: 'thin', color: { argb: '000000' } },
              left: [1, 2, 6, 18, 22].includes(col) ? { style: 'thin', color: { argb: '000000' } } : undefined,
              right: [1, 5, 17, 21, 25].includes(col) ? { style: 'thin', color: { argb: '000000' } } : undefined
            };
          } else if (isLastRow) { // 最終行 → 下、左、右の罫線を適用
            borderStyle = {
              bottom: { style: 'thin', color: { argb: '000000' } },
              left: [1, 2, 6, 18, 22].includes(col) ? { style: 'thin', color: { argb: '000000' } } : undefined,
              right: [1, 5, 17, 21, 25].includes(col) ? { style: 'thin', color: { argb: '000000' } } : undefined
            };
          } else { // 中間行 → 左右のみ罫線を適用（内部罫線なし）
            borderStyle = {
              left: [1, 2, 6, 18, 22].includes(col) ? { style: 'thin', color: { argb: '000000' } } : undefined,
              right: [1, 5, 17, 21, 25].includes(col) ? { style: 'thin', color: { argb: '000000' } } : undefined
            };
          }

          cell.border = borderStyle;
        }

        row.commit();
        rowIndex++; // 次の行へ移動
      }
    });

    // 印刷範囲を自動設定
    worksheet2.pageSetup.printArea = `A1:Y${rowIndex - 1}`;
    worksheet2.pageSetup.horizontalCentered = true;
    worksheet2.pageSetup.scale = 60

    // 1行～5行 は高さ 18 に設定
    for (let row = 1; row <= 5; row++) {
      worksheet2.getRow(row).height = 18;
    }

    // その他の行は 13.5 に設定 (6~最後)
    for (let row = 6; row < rowIndex; row++) {
      worksheet2.getRow(row).height = 13.5;
    }


    /**--------------------------------------------------------------------------------------------
     * シート3「テクニカル・スキルシート」
     * 
     */

    const worksheet3 = workbook.addWorksheet('添付資料', {
      views: [
        {
          style: 'pageBreakPreview',
          showGridLines: false // 目盛線を削除
        }
      ],
      pageSetup: { paperSize: 9, orientation: 'portrait' } // A4サイズ
    });

    worksheet3.pageSetup.margins = {
      left: this.cmToInch(1.8), right: this.cmToInch(1.8),
      top: this.cmToInch(1.9), bottom: this.cmToInch(1.9),
      header: 0, footer: 0
    };

    // 目盛線を非表示に設定
    worksheet3.properties.showGridLines = false;

    const bClassOrder = [
      'OS / NOS',
      'システム設計構築',
      'システム管理',
      'システム運用',
      'サーバ技術',
      'ＮＷ技術',
      'クラウド仮想環境'
    ];

    const iClassOrder = [
      '開発言語',
      'ＤＢ',
      'グループ',
      '暗号化/情報漏えい対策',
      '取得資格',
      '語学',
      'ヒューマンスキル'
    ];

    // データ配列を確認し、変換
    const data = Array.isArray(skillSheetData.skillSheetData) ? skillSheetData.skillSheetData : [];

    const orderedBData = bClassOrder.flatMap(className =>
      data.filter(item => item.classification === className)
    );

    const orderedIData = iClassOrder.flatMap(className =>
      data.filter(item => item.classification === className)
    );

    let targetClassification = ''

    const licenses = orderedIData.filter(item => item.classification === '取得資格')
    const languages = orderedIData.filter(item => item.classification === '語学')

    let insertAfterClassification = '暗号化/情報漏えい対策';

    if (licenses.length < 6) {
      targetClassification = '取得資格'; // 追加対象の分類
      insertAfterClassification = '暗号化/情報漏えい対策';
      const toAdd = 6 - licenses.length; // 不足分の数を算出

      // 空白のダミーデータを生成
      const dummyItems = Array.from({ length: toAdd }, () => ({
        classification: targetClassification,
        name: '',
        skillLevel: ''
      }));

      // すでに取得資格が存在する場合、その最後のインデックスを取得
      const lastIndex = orderedIData
        .map((item, idx) => item.classification === targetClassification ? idx : -1)
        .filter(idx => idx !== -1)
        .pop();

      if (lastIndex !== undefined) {
        // 最後の取得資格の後にダミーデータを挿入
        orderedIData.splice(lastIndex + 1, 0, ...dummyItems);
      } else {
        // 取得資格が存在しない場合、「暗号化/情報漏えい対策」の後に挿入
        const insertAfterIndex = orderedIData
          .map((item, idx) => item.classification === insertAfterClassification ? idx : -1)
          .filter(idx => idx !== -1)
          .pop();

        if (insertAfterIndex !== undefined) {
          // 挿入対象分類の直後に挿入
          orderedIData.splice(insertAfterIndex + 1, 0, ...dummyItems);
        }
      }
    }

    // 語学が4個以下の場合、空の語学追加
    if (languages.length < 4) {
      targetClassification = '語学' // 追加対象の分類
      insertAfterClassification = '取得資格';
      const toAdd = 4 - languages.length; // 不足分の数を算出

      // 空白のダミーデータを生成
      const dummyItems = Array.from({ length: toAdd }, () => ({
        classification: targetClassification,
        name: targetClassification,
        skillLevel: ''
      }));

      // すでに語学が存在する場合、その最後のインデックスを取得
      const lastIndex = orderedIData
        .map((item, idx) => item.classification === targetClassification ? idx : -1)
        .filter(idx => idx !== -1)
        .pop();

      if (lastIndex !== undefined) {
        // 最後の語学の後にダミーデータを挿入
        orderedIData.splice(lastIndex + 1, 0, ...dummyItems);
      } else {
        // 語学が存在しない場合、「取得資格」の後に挿入
        const insertAfterIndex = orderedIData
          .map((item, idx) => item.classification === insertAfterClassification ? idx : -1)
          .filter(idx => idx !== -1)
          .pop();

        if (insertAfterIndex !== undefined) {
          // 挿入対象分類の直後に挿入
          orderedIData.splice(insertAfterIndex + 1, 0, ...dummyItems);
        }
      }
    }


    // sheetId を基準にソート
    // data.sort((a, b) => a.sheetId - b.sheetId);

    // 列の幅を設定
    const columnWidths = {
      1: 1.60, // A列 0.88
      2: 3.60, // B列 2.78
      3: 26.90, // C列 26.11
      4: 5.10, // D列 4.33
      5: 5.10, // E列 4.33
      6: 5.10, // F列 4.33
      7: 5.10, // G列 4.33
      8: 4.10, // H列 3.33
      9: 3.60, // I列 2.78
      10: 26.90, // J列 26.11
      11: 5.10, // K列 4.33
      12: 5.10, // L列 4.33
      13: 5.10, // M列 4.33
      14: 5.10  // N列 4.33
    };

    Object.keys(columnWidths).forEach(col => {
      worksheet3.getColumn(parseInt(col)).width = columnWidths[col];
    });

    // E1に テクニカル・スキルシート 追加
    const titleCell3 = worksheet3.getCell('E1');
    titleCell3.value = 'テクニカル・スキルシート';
    titleCell3.font = { name: 'ＭＳ Ｐゴシック', bold: true, size: 14 };
    titleCell3.alignment = { vertical: 'middle' };

    // 候
    worksheet3.getRow(1).height = 30; // 30
    worksheet3.getRow(2).height = 103.5; // 103.5
    for (let i = 3; i < 66; i++) {
      worksheet3.getRow(i).height = 13.5; // 13.5
    }
    worksheet3.getRow(66).height = 14.3; // 14.3
    worksheet3.getRow(67).height = 14.3; // 14.3
    worksheet3.getRow(68).height = 18; // 18
    worksheet3.getRow(69).height = 18; // 18

    // ヘッダー追加
    const headerRow = worksheet3.getRow(2);
    const headers = ['分\n\n類', '', '基\n本\n知\n識\nの\nみ', '実\n践\n経\n験\nあ\nる', '一\n人\nで\n実\n践\n可\n能', 'ユ\n｜\nザ\n指\n導\n可\n能', '', '分\n\n類', '', '基\n本\n知\n識\nの\nみ', '実\n践\n経\n験\nあ\nる', '一\n人\nで\n実\n践\n可\n能', 'ユ\n｜\nザ\n指\n導\n可\n能'];

    headers.forEach((text, index) => {
      if (index === 6) {
        return
      }
      else if (index === 1 || index === 8) {
        let cell = headerRow.getCell(index + 2);
        cell.value = {
          richText: [
            { text: '\n\n名　称\n\n\n', font: { name: 'ＭＳ Ｐゴシック', size: 11, color: { argb: '000000' } } },
            { text: '（右下の該当セルに○を付ける）', font: { name: 'ＭＳ Ｐゴシック', size: 8, color: { argb: 'FF0000' } } }
          ]
        };
        cell.alignment = { horizontal: 'center', vertical: 'middle', wrapText: true };
        cell.border = {
          top: { style: 'thin', color: { argb: '000000' } },
          left: { style: 'thin', color: { argb: '000000' } },
          right: { style: 'thin', color: { argb: '000000' } },
          bottom: { style: 'thin', color: { argb: '000000' } }

        }
      }
      else {
        let cell = headerRow.getCell(index + 2);
        cell.value = text;
        cell.font = { name: 'ＭＳ Ｐゴシック', size: 11 };
        cell.alignment = { horizontal: 'center', vertical: 'middle', wrapText: true };
        cell.fill = (index === 0 || index === 7) ? { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFF99' } } : { type: 'pattern', pattern: 'solid', fgColor: { argb: 'CCFFFF' } };
        cell.border = {
          top: { style: 'thin', color: { argb: '000000' } },
          left: { style: 'thin', color: { argb: '000000' } },
          right: { style: 'thin', color: { argb: '000000' } },
          bottom: { style: 'thin', color: { argb: '000000' } }
        };
      }
    });
    headerRow.commit();

    const skillMap = {
      'ユーザー指導可能': 7, // G列
      '一人で実践可能': 6, // F列
      '実践経験ある': 5,  // E列
      '基本知識のみ': 4   // D列
    };

    const humanSkillMap = {
      '超得意': 7, // G列
      '得意': 6, // F列
      '人並み': 5,  // E列
      '不得意': 4   // D列
    };

    let rowIndex3 = 3;

    let rowIndexB = 3;
    let classificationMergeStart = rowIndexB;
    let prevClassification = orderedBData[0]?.classification;
    let columnLetter = 'B';

    let rowIndexI = 3;
    let classificationMergeStartI = rowIndexI;
    let prevClassificationI = orderedIData[0]?.classification;
    let columnLetterI = 'I';
    let humanskillHeaderInserted = false;

    const allSkillColsLeft = [4, 5, 6, 7]; // C~F 열 (기본知, 実践, 一人, ユーザ)
    const allSkillColsRight = [11, 12, 13, 14]; // K~N 열 번호

    orderedBData.forEach((item, i) => {
      if (i === 0 || item.classification !== prevClassification) {
        if (i > 0 && rowIndexB - classificationMergeStart > 1) {
          worksheet3.mergeCells(`${columnLetter}${classificationMergeStart}:${columnLetter}${rowIndexB - 1}`);

          const prevRow = worksheet3.getRow(rowIndexB - 1);
          for (let col = 2; col <= 7; col++) {
            const cell = prevRow.getCell(col);
            cell.border = {
              ...cell.border,
              bottom: { style: 'thin', color: { argb: '000000' } }
            };
          }
        }
        classificationMergeStart = rowIndexB;
        prevClassification = item.classification;
      }

      const cell = worksheet3.getCell(`${columnLetter}${rowIndexB}`);
      cell.value = item.classification
      cell.font = { name: 'ＭＳ Ｐゴシック', size: item.classification === "クラウド仮想環境" ? 9 : 11 };
      cell.alignment = {
        vertical: 'middle', horizontal: 'center', wrapText: item.classification === "システム運用" ? false : true,
        textRotation: "vertical" // 縦書き
      };
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFF99' } };
      cell.border = {
        top: { style: 'thin', color: { argb: '000000' } },
        left: { style: 'thin', color: { argb: '000000' } },
        right: { style: 'thin', color: { argb: '000000' } },
        bottom: { style: 'thin', color: { argb: '000000' } }
      };

      // スキルシート「左」：item.nameは C列(3), ○表示は D~G列(4~7)
      worksheet3.getRow(rowIndex3).getCell(3).value = item.name;

      const nameCell = worksheet3.getRow(rowIndex3).getCell(3)
      nameCell.font = { name: 'ＭＳ Ｐゴシック', size: 11 };
      nameCell.alignment = { vertical: 'middle', horizontal: 'left', wrapText: false };
      nameCell.border = {
        top: { style: 'hair', color: { argb: '000000' } },
        left: { style: 'thin', color: { argb: '000000' } },
        right: { style: 'thin', color: { argb: '000000' } },
        bottom: { style: 'hair', color: { argb: '000000' } }
      };

      // for文で全てのセルにスタイル適用
      allSkillColsLeft.forEach(col => {
        const cell = worksheet3.getRow(rowIndexB).getCell(col);
        cell.value = '';
        cell.font = { name: 'ＭＳ Ｐゴシック', size: 11 };
        cell.alignment = { horizontal: 'center', vertical: 'middle' };
        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'CCFFFF' } };
        cell.border = {
          top: { style: 'hair', color: { argb: '000000' } },
          left: { style: 'thin', color: { argb: '000000' } },
          right: { style: 'thin', color: { argb: '000000' } },
          bottom: { style: 'hair', color: { argb: '000000' } }
        };
      });

      // skillLevelが存在する場合、 ○ 表示
      if (skillMap[item.skillLevel]) {
        const markCol = skillMap[item.skillLevel]; // D~G (4~7)
        const markCell = worksheet3.getRow(rowIndex3).getCell(markCol);
        markCell.value = '○';
      }
      rowIndex3++;
      rowIndexB++;
    });

    // 最後のマージ
    if (rowIndexB - classificationMergeStart > 1) {
      worksheet3.mergeCells(`${columnLetter}${classificationMergeStart}:${columnLetter}${rowIndexB - 1}`);

      const lastRow = worksheet3.getRow(rowIndexB - 1);
      for (let col = 2; col <= 7; col++) {
        const cell = lastRow.getCell(col);
        cell.border = {
          ...cell.border,
          bottom: { style: 'thin', color: { argb: '000000' } }
        };
      }
    }


    // 右のスキルシート
    orderedIData.forEach((item, i) => {
      if (i === 0 || item.classification !== prevClassificationI) {
        if (i > 0 && rowIndexI - classificationMergeStartI > 1) {
          worksheet3.mergeCells(`${columnLetterI}${classificationMergeStartI}:${columnLetterI}${rowIndexI - 1}`);

          const prevRow = worksheet3.getRow(rowIndexI - 1);
          for (let col = 9; col <= 14; col++) {
            const cell = prevRow.getCell(col);
            cell.border = {
              ...cell.border,
              bottom: { style: 'thin', color: { argb: '000000' } }
            };
          }
        }

        classificationMergeStartI = rowIndexI;
        prevClassificationI = item.classification;
      }

      const cell = worksheet3.getCell(`${columnLetterI}${rowIndexI}`);
      cell.value = item.classification

      cell.font = { name: 'ＭＳ Ｐゴシック', size: item.classification === "ヒューマンスキル" ? 9 : item.classification === "グループ" ? 10 : 11 };
      cell.alignment = {
        vertical: 'middle', horizontal: 'center', wrapText: item.classification === "ヒューマンスキル" ? false : true,
        textRotation: "vertical" // 縦書き
      };
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFF99' } };
      cell.border = {
        top: { style: 'hair', color: { argb: '000000' } },
        left: { style: 'thin', color: { argb: '000000' } },
        right: { style: 'thin', color: { argb: '000000' } },
        bottom: { style: 'hair', color: { argb: '000000' } }
      };

      if (item.classification === "取得資格") {
        worksheet3.getRow(rowIndexI).getCell(10).value = item.name;
        worksheet3.getRow(rowIndexI).getCell(11).value = item.skillLevel;
        const cellJ = worksheet3.getRow(rowIndexI).getCell(10);
        cellJ.border = {
          top: { style: 'thin', color: { argb: '000000' } },
          left: { style: 'thin', color: { argb: '000000' } },
          right: { style: 'thin', color: { argb: '000000' } },
          bottom: { style: 'thin', color: { argb: '000000' } }
        };
        // J列〜N列 (10〜14) 融合
        worksheet3.mergeCells(`K${rowIndexI}:N${rowIndexI}`);

        const mergedCell = worksheet3.getCell(`J${rowIndexI}`);
        mergedCell.font = { name: 'ＭＳ Ｐゴシック', size: 11 };
        mergedCell.alignment = { vertical: 'middle', horizontal: 'left', wrapText: false };
        mergedCell.border = {
          top: { style: 'hair', color: { argb: '000000' } },
          left: { style: 'thin', color: { argb: '000000' } },
          right: { style: 'thin', color: { argb: '000000' } },
          bottom: { style: 'hair', color: { argb: '000000' } }
        };
        allSkillColsRight.forEach(col => {
          const cell = worksheet3.getRow(rowIndexI).getCell(col);
          cell.font = { name: 'ＭＳ Ｐゴシック', size: 11 };
          cell.alignment = { horizontal: 'left', vertical: 'middle' };
          cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'CCFFFF' } };
          cell.border = {
            top: { style: 'hair', color: { argb: '000000' } },
            left: { style: 'thin', color: { argb: '000000' } },
            right: { style: 'thin', color: { argb: '000000' } },
            bottom: { style: 'hair', color: { argb: '000000' } }
          };
        });
      }
      else if (item.classification === "語学") {
        // J列〜N列 (10〜14) 融合
        worksheet3.mergeCells(`J${rowIndexI}:N${rowIndexI}`);

        const mergedCell = worksheet3.getCell(`J${rowIndexI}`);
        mergedCell.font = { name: 'ＭＳ Ｐゴシック', size: 11 };
        mergedCell.alignment = { vertical: 'middle', horizontal: 'left', wrapText: true };
        mergedCell.border = {
          top: { style: 'hair', color: { argb: '000000' } },
          left: { style: 'thin', color: { argb: '000000' } },
          right: { style: 'thin', color: { argb: '000000' } },
          bottom: { style: 'hair', color: { argb: '000000' } }
        };
        allSkillColsRight.forEach(col => {
          const cell = worksheet3.getRow(rowIndexI).getCell(col);
          cell.value = item.skillLevel;
          cell.font = { name: 'ＭＳ Ｐゴシック', size: 11 };
          cell.alignment = { horizontal: 'left', vertical: 'middle' };
          cell.border = {
            top: { style: 'hair', color: { argb: '000000' } },
            left: { style: 'thin', color: { argb: '000000' } },
            right: { style: 'thin', color: { argb: '000000' } },
            bottom: { style: 'hair', color: { argb: '000000' } }
          };
        });
      }
      else if (item.classification === "ヒューマンスキル") {

        if (!humanskillHeaderInserted) {
          const headerRow = worksheet3.getRow(rowIndexI);

          // J列: 空のセル
          const cellJ = headerRow.getCell(10);
          cellJ.value = '';
          cellJ.font = { name: 'ＭＳ Ｐゴシック', size: 10 };
          cellJ.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'D9D9D9' } };
          cellJ.border = {
            top: { style: 'thin', color: { argb: '000000' } },
            left: { style: 'thin', color: { argb: '000000' } },
            right: { style: 'thin', color: { argb: '000000' } },
            bottom: { style: 'thin', color: { argb: '000000' } }
          };

          // K~N列: 不得意, 人並み, 得意, 超得意
          const headers = ['不得意', '人並み', '得意', '超得意'];
          headers.forEach((text, index) => {
            const cell = headerRow.getCell(11 + index); // 11 = K열
            cell.value = text;
            cell.font = { name: 'ＭＳ Ｐゴシック', size: 8 };
            cell.alignment = { vertical: 'middle', horizontal: 'center', wrapText: false };
            cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'CCFFFF' } };
            cell.border = {
              top: { style: 'thin', color: { argb: '000000' } },
              left: { style: 'thin', color: { argb: '000000' } },
              right: { style: 'thin', color: { argb: '000000' } },
              bottom: { style: 'thin', color: { argb: '000000' } }
            };
          });

          humanskillHeaderInserted = true;
          rowIndexI++; // 次の行に
        }

        // 名称追加
        worksheet3.getRow(rowIndexI).getCell(10).value = item.name;
        const cell = worksheet3.getCell(`J${rowIndexI}`);

        cell.font = { name: 'ＭＳ Ｐゴシック', size: 11 };
        cell.alignment = { vertical: 'middle', horizontal: 'left', wrapText: true };
        cell.border = {
          top: { style: 'hair', color: { argb: '000000' } },
          left: { style: 'thin', color: { argb: '000000' } },
          right: { style: 'thin', color: { argb: '000000' } },
          bottom: { style: 'hair', color: { argb: '000000' } }
        }

        // ○ 位置設定
        if (humanSkillMap[item.skillLevel]) {
          const markCol = humanSkillMap[item.skillLevel] + 7; // K~N → skillMap 値(4~7) + 7 = 11~14
          const markCell = worksheet3.getRow(rowIndexI).getCell(markCol);
          markCell.value = '';
          markCell.font = { name: 'ＭＳ Ｐゴシック', size: 11 };
          markCell.alignment = { vertical: 'middle', horizontal: 'left', wrapText: true };
          markCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'CCFFFF' } };
          markCell.border = {
            top: { style: 'hair', color: { argb: '000000' } },
            left: { style: 'thin', color: { argb: '000000' } },
            right: { style: 'thin', color: { argb: '000000' } },
            bottom: { style: 'hair', color: { argb: '000000' } }
          }
          allSkillColsRight.forEach(col => {
            const cell = worksheet3.getRow(rowIndexI).getCell(col);
            cell.value = '';
            cell.font = { name: 'ＭＳ Ｐゴシック', size: 11 };
            cell.alignment = { horizontal: 'center', vertical: 'middle' };
            cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'CCFFFF' } };
            cell.border = {
              top: { style: 'hair', color: { argb: '000000' } },
              left: { style: 'thin', color: { argb: '000000' } },
              right: { style: 'thin', color: { argb: '000000' } },
              bottom: { style: 'hair', color: { argb: '000000' } }
            };
          });

          // ○ 位置設定
          if (humanSkillMap[item.skillLevel]) {
            const markCol = humanSkillMap[item.skillLevel] + 7; // K~N → skillMap 値(4~7) + 7 = 11~14
            const markCell = worksheet3.getRow(rowIndexI).getCell(markCol);
            markCell.value = '○';
          }

        }
      }
      else {
        worksheet3.getRow(rowIndexI).getCell(10).value = item.name;
        const cell = worksheet3.getCell(`J${rowIndexI}`);

        cell.font = { name: 'ＭＳ Ｐゴシック', size: 11 };
        cell.alignment = { vertical: 'middle', horizontal: 'left', wrapText: false };
        cell.border = {
          top: { style: 'hair', color: { argb: '000000' } },
          left: { style: 'thin', color: { argb: '000000' } },
          right: { style: 'thin', color: { argb: '000000' } },
          bottom: { style: 'hair', color: { argb: '000000' } }
        }

        allSkillColsRight.forEach(col => {
          const cell = worksheet3.getRow(rowIndexI).getCell(col);
          cell.value = '';
          cell.font = { name: 'ＭＳ Ｐゴシック', size: 11 };
          cell.alignment = { horizontal: 'center', vertical: 'middle' };
          cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'CCFFFF' } };
          cell.border = {
            top: { style: 'hair', color: { argb: '000000' } },
            left: { style: 'thin', color: { argb: '000000' } },
            right: { style: 'thin', color: { argb: '000000' } },
            bottom: { style: 'hair', color: { argb: '000000' } }
          };
        });

        // ○ 位置設定
        if (skillMap[item.skillLevel]) {
          const markCol = skillMap[item.skillLevel] + 7; // K~N → skillMap 値(4~7) + 7 = 11~14
          const markCell = worksheet3.getRow(rowIndexI).getCell(markCol);
          markCell.value = '○';
        }
      }
      rowIndex3++
      rowIndexI++;
    });

    // 最後のマージ
    if (rowIndexI - classificationMergeStartI > 1) {
      worksheet3.mergeCells(`${columnLetterI}${classificationMergeStartI}:${columnLetterI}${rowIndexI - 1}`);

      const lastRow = worksheet3.getRow(rowIndexI - 1);
      for (let col = 9; col <= 14; col++) {
        const cell = lastRow.getCell(col);
        cell.border = {
          ...cell.border,
          bottom: { style: 'thin', color: { argb: '000000' } }
        };
      }
    }


    worksheet3.pageSetup.fitToPage = true;
    worksheet3.pageSetup.horizontalCentered = true;
    worksheet3.pageSetup.printArea = `A1:N69`;

    // Excelファイルを保存
    const buffer = await workbook.xlsx.writeBuffer();
    const today = new Date().toISOString().split("T")[0]; // YYYY-MM-DD
    const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });

    saveAs(blob, `スキルシート_${staffName}_${today}.xlsx`);
  },


//   /**
//  * 指定された列と行の範囲から、セルのテキストを複数行にわたって抽出し、
//  * 改行で結合した文字列として返す。
//  *
//  * @param {number} colIndex - 対象となる列番号（1始まり, 例: A列 → 1）
//  * @param {number} startRow - 開始行番号（1始まり）
//  * @param {number} endRow - 終了行番号（1始まり）
//  * @param {Worksheet} worksheet - 対象のExcelJSワークシートインスタンス
//  * @returns {string} - 結合されたテキスト（空白行は無視、改行区切り）
//  */
//   extractMergedCellText(colIndex, startRow, endRow, worksheet) {
//     const lines = []
//     for (let i = startRow; i <= endRow; i++) {
//       const cellText = worksheet.getRow(i).getCell(colIndex)?.text?.trim()
//       if (cellText) lines.push(cellText)
//     }
//     return lines.join('\n')
//   },

  /**
   * 開始行番から終了行番までのデータを取得（結合セールも対応）
   * 
   * @param {*} worksheet - 対象となるワークシート
   * @param {*} startRow - 開始行番
   * @param {*} endRow - 終了行番
   * @returns 
   */
  extractMultiline(worksheet, startRow, endRow, startCol = null, endCol = null) {
    if (!startCol) startCol = 1 // startColが未指定時には1指定
    if (!endCol) endCol = worksheet.columnCount // endColが未指定時にはワークシートのカラム数

    const lines = []
    for (let rowNum = startRow; rowNum <= endRow; rowNum++) {
      const row = worksheet.getRow(rowNum)
      const rowTexts = []
      
      for (let colNum = startCol; colNum <= endCol; colNum++) {
        const cell = row.getCell(colNum)
        if (cell.isMerged && cell !== cell.master) {
          rowTexts[colNum - 1] = ''; // 結合セールはマスタ以外無視
        } else {
          const actualCell = cell.isMerged ? cell.master : cell
          const text = (actualCell.text || '')
          rowTexts[colNum - 1] = text.trim();
        }
      }

      lines.push(rowTexts.join(' ').trim())
    }

    return lines.join('\n').trim()
  },



  /**
  * Excelファイルからシート2（業務関連）のデータを解析し、
  * DBに保存する形式で返却します。
  * @param {File} excelFile - アップロードされたExcelファイル
  * @param {number} staffId - career_idとして使用するスタッフID
  * @param {string} userName - ユーザー名（更新者登録のため）
  * @returns {response} - 結果レスポンス
  */
  async createSkillSheet(excelFile, staffId, userName) {
    const workbook = new ExcelJS.Workbook()
    const buffer = await excelFile.arrayBuffer()
    await workbook.xlsx.load(buffer)
    const findWorksheetByKeywords = (keywords) => {
      return workbook.worksheets.find(sheet =>
        keywords.some(keyword => sheet.name.includes(keyword))
      );
    };


    /**--------------------------------------------------------------------------------------------------------------------------
     * シート１「職務経歴書」
     * 
     *  */
    const worksheet1 = findWorksheetByKeywords(['職務経歴書', '職歴', '経歴'])
    if (!worksheet1) {
      throw new Error('「職務経歴書」を含むシートが見つかりません')
    }

    const name = worksheet1.getRow(5).getCell(3)?.text?.trim() || ''
    const academic = worksheet1.getRow(5).getCell(17)?.text?.trim() || ''
    const nearestStation = worksheet1.getRow(6).getCell(17)?.text?.trim() || ''
  
    const rawText = this.extractMultiline(worksheet1, 6, 6, 4, 8).trim();

    const [genderRaw, ageRaw] = rawText.split('/').map(s => s.trim());
    const gender = genderRaw || '';
    const age = ageRaw ? parseInt(ageRaw.replace('歳', '').trim()) : null;
    
    const sheet1Headers = {}
    Object.keys(worksheet1._merges).forEach(key => {
      const range = worksheet1._merges[key];
      const master = worksheet1.getCell(key);
      if (master.text == "略歴") {
        sheet1Headers["biography"] = range
      } else if (master.text == "自己PR") {
        sheet1Headers["selfPr"] = range
      } else if (master.text == "得意分野") {
        sheet1Headers["specialtyField"] = range
      } else if (master.text == "保有資格") {
        sheet1Headers["qualification"] = range
      }
    })
    
    const biography = this.extractMultiline(worksheet1, sheet1Headers["biography"]["top"]+1, sheet1Headers["selfPr"]["top"]-1)
    const selfPr = this.extractMultiline(worksheet1, sheet1Headers["selfPr"]["top"]+1, sheet1Headers["specialtyField"]["top"]-1)
    const specialtyField = this.extractMultiline(worksheet1, sheet1Headers["specialtyField"]["top"]+1, sheet1Headers["qualification"]["top"]-1)
    const qualification = this.extractMultiline(worksheet1, sheet1Headers["qualification"]["top"]+1, worksheet1.rowCount)
  
    const careerData = {
      staffId: staffId,
      name: name,
      location: nearestStation,
      gender: gender,
      age: age,
      finalEducationalBackground: academic,
      summary: biography,
      selfPR: selfPr,
      specialty: specialtyField,
      qualifications: qualification,
      userName: userName
    }
  

      /**------------------------------------------------------------------------------------------------------------------------------
     * シート２ 「職務経歴書」
     * 
     */
    const worksheet2 = findWorksheetByKeywords(['業務関連', '業務', '関連'])
    if (!worksheet2) {
      throw new Error('「業務関連」シートが見つかりません')
    }

    const sheet2Headers = {}
    Object.keys(worksheet2._merges).forEach(key => {
      const range = worksheet2._merges[key];
      const master = worksheet2.getCell(key);
      if (master.text == "開発期間") {
        sheet2Headers["period"] = range
      } else if (master.text == "業務内容") {
        sheet2Headers["business"] = range
      } else if (master.text == "開発環境等") {
        sheet2Headers["environment"] = range
      } else if (master.text == "役割") {
        sheet2Headers["role"] = range
      }
    })
  
    const careerItemsData = []
    let rowIndex = sheet2Headers["period"]["top"] + 1 // 6行？
  
    while (rowIndex <= worksheet2.rowCount) {
      const startRow = rowIndex
      let endRow = startRow
  
      while (endRow <= worksheet2.rowCount) {
        const row = worksheet2.getRow(endRow)
        const border = row.getCell(1).style?.border
        const isBottomThin = border?.bottom?.style === 'thin'
        if (isBottomThin) break
        endRow++
      }
  
      const row = worksheet2.getRow(startRow)
  
      const index = row.getCell(1)?.text?.trim() || ''
  
      const periodRaw = this.extractMultiline(worksheet2, startRow, endRow, sheet2Headers["period"]["left"], sheet2Headers["period"]["right"])
      const startMatch = periodRaw.match(/(\d{4})年(\d{1,2})月(?:([0-9]{1,2})日)?/)
      const endMatch = periodRaw.match(/～\s*(\d{4})年(\d{1,2})月(?:([0-9]{1,2})日)?/)
      const totalMatch = periodRaw.match(/（(.+?)）/)
    
      const startPeriod = startMatch
        ? `${startMatch[1]}-${startMatch[2].padStart(2, '0')}-${startMatch[3] ? startMatch[3].padStart(2, '0') : '01'}`
        : null
    
      const endPeriod = endMatch
        ? `${endMatch[1]}-${endMatch[2].padStart(2, '0')}-${endMatch[3] ? endMatch[3].padStart(2, '0') : new Date(endMatch[1], endMatch[2], 0).getDate()}`
        : null
    
      const totalPeriod = totalMatch ? totalMatch[1] : ''
  
      const details = this.extractMultiline(worksheet2, startRow, endRow, sheet2Headers["business"]["left"], sheet2Headers["business"]["right"])
    
      const environment = this.extractMultiline(worksheet2, startRow, endRow, sheet2Headers["environment"]["left"], sheet2Headers["environment"]["right"])
      const role = this.extractMultiline(worksheet2, startRow, endRow, sheet2Headers["role"]["left"], sheet2Headers["role"]["right"])
  
      const hasAnyContent =
          index || startPeriod || endPeriod || totalPeriod || details || environment || role

        if (hasAnyContent) {
          careerItemsData.push({
            index,
            startPeriod,
            endPeriod,
            totalPeriod,
            details,
            environment,
            role
          })
        }
      rowIndex = endRow + 1
    }




    /**--------------------------------------------------------------------------------------------
     * シート3「テクニカル・スキルシート」
     * 
     */
      const worksheet3 = findWorksheetByKeywords(['添付資料', '添付', '資料'])
      if (!worksheet3) {
        throw new Error('「添付資料」シートが見つかりません')
      }

      const skillSheetsData = []
      const mergedRanges = worksheet3._merges || []
      const categoryMap = {}

      for (const key in mergedRanges) {
        const { top, bottom, left } = mergedRanges[key].model
        if (left === 2 || left === 9) {
          const value = worksheet3.getRow(top).getCell(left)?.text?.trim() || ''
          for (let i = top; i <= bottom; i++) {
            categoryMap[`${left}-${i}`] = value
          }
        }
      }

      // スキルレベルのマッピング
      const skillLevelMap = ['基本知識のみ', '実践経験ある', '一人で実践可能', 'ユーザ指導可能']
      const humanSkillMap = ['不得意', '人並み', '得意', '超得意']

      // ⬅️ 左側: B3~G69
      for (let row = 3; row <= 69; row++) {
        const category = categoryMap[`2-${row}`] || worksheet3.getRow(row).getCell(2)?.text?.trim()
        const name = worksheet3.getRow(row).getCell(3)?.text?.trim()
        let skillLevel = ''

        for (let col = 4; col <= 7; col++) {
          const cell = worksheet3.getRow(row).getCell(col);
          const cellValue = cell?.value ?? ''; // null-safe
          const cellText = typeof cellValue === 'string' ? cellValue.trim()
            : typeof cellValue === 'number' ? String(cellValue).trim()
            : '';

          if (cellText === '○') {
            skillLevel = skillLevelMap[col - 4]
            break
          }
        }

        if (category && name) {
          let fixedCategory = category;
        
          if (category === 'O\nS\n/\nN\nO\nS') {
            fixedCategory = 'OS / NOS';
          } else if (category === 'シ\nス\nテ\nム\n設\n計\n構\n築') {
            fixedCategory = 'システム設計構築';
          } else if (category === 'シ\nス\nテ\nム\n管\n理') {
            fixedCategory = 'システム管理';
          } else if (category === 'シ\nス\nテ\nム\n運\n用') {
            fixedCategory = 'システム運用';
          } else if (category === 'サ\n｜\nバ\n技\n術') {
            fixedCategory = 'サーバ技術';
          } else if (category === 'ＮＷ\n技\n術') {
            fixedCategory = 'ＮＷ技術';
          } else if (category === 'クラウド仮想環境') {
            fixedCategory = 'クラウド仮想環境';
          } else if (category === '開\n発\n言\n語') {
            fixedCategory = '開発言語';
          } else if (category === 'ＤＢ') {
            fixedCategory = 'ＤＢ';
          } else if (category === 'グループ') {
            fixedCategory = 'グループ';
          } else if (category === '暗号化/情報漏えい対策') {
            fixedCategory = '暗号化/情報漏えい対策';
          } else if (category === '取\n得\n資\n格') {
            fixedCategory = '取得資格';
          } else if (category === '語\n学') {
            fixedCategory = '語学';
          } else if (category === 'ヒ\nュ\n｜\nマ\nン\nス\nキ\nル') {
            fixedCategory = 'ヒューマンスキル';
          }
          

          if (fixedCategory === '語学') {
            skillSheetsData.push({
              category: fixedCategory,
              name: fixedCategory,
              skillLevel: name,
              });
            } else {
              skillSheetsData.push({
              category: fixedCategory,
              name,
              skillLevel,
              });
            }
          }
        }

      // ➡️ 右側: I3~N64
      for (let row = 3; row <= 64; row++) {
        if (row === 58) continue // 58行目はスキップ

        const category = categoryMap[`9-${row}`] || worksheet3.getRow(row).getCell(9)?.text?.trim()
        let name = worksheet3.getRow(row).getCell(10)?.text?.trim()
        let skillLevel = ''

        if (row >= 48 && row <= 53) {
          const cellName = worksheet3.getRow(row).getCell(10);
          const cellSkill = worksheet3.getRow(row).getCell(11);

          const nameText = cellName?.text ?? "";
          name = nameText.trim();

          const skillText = cellSkill?.text ?? "";
          skillLevel = skillText.trim();
        } else if (row >= 54 && row <= 57) {
          const cell = worksheet3.getRow(row).getCell(11);
          const skillValue = cell?.value;
          skillLevel = skillValue != null ? String(skillValue).trim() : "";
        } else if (row >= 59) {
          // ヒューマンスキル
          for (let col = 11; col <= 14; col++) {
            const cell = worksheet3.getRow(row).getCell(col);
            const cellValue = cell?.value ?? ''; // null-safe
            const cellText = typeof cellValue === 'string' ? cellValue.trim()
              : typeof cellValue === 'number' ? String(cellValue).trim()
              : '';

          if (cellText === '○') {
              skillLevel = humanSkillMap[col - 11]
              break
            }
          }
        } else {
          // 一般の右側スキル領域
          for (let col = 11; col <= 14; col++) {
            const cell = worksheet3.getRow(row).getCell(col);
            const cellValue = cell?.value ?? ''; // null-safe
            const cellText = typeof cellValue === 'string' ? cellValue.trim()
              : typeof cellValue === 'number' ? String(cellValue).trim()
              : '';

          if (cellText === '○') {
              skillLevel = skillLevelMap[col - 11]
              break
            }
          }
        }

        if (category && name) {
          let fixedCategory = category;
        
            if (category === 'O\nS\n/\nN\nO\nS') {
                fixedCategory = 'OS / NOS';
              } else if (category === 'シ\nス\nテ\nム\n設\n計\n構\n築') {
                fixedCategory = 'システム設計構築';
              } else if (category === 'シ\nス\nテ\nム\n管\n理') {
                fixedCategory = 'システム管理';
              } else if (category === 'シ\nス\nテ\nム\n運\n用') {
                fixedCategory = 'システム運用';
              } else if (category === 'サ\n｜\nバ\n技\n術') {
                fixedCategory = 'サーバ技術';
              } else if (category === '1') {
                fixedCategory = 'ＮＷ技術';
              } else if (category === 'クラウド仮想環境') {
                fixedCategory = 'クラウド仮想環境';
              } else if (category === '開\n発\n言\n語') {
                fixedCategory = '開発言語';
              } else if (category === 'ＤＢ') {
                fixedCategory = 'ＤＢ';
              } else if (category === 'グループ') {
                fixedCategory = 'グループ';
              } else if (category === '暗号化/情報漏えい対策') {
                fixedCategory = '暗号化/情報漏えい対策';
              } else if (category === '取\n得\n資\n格') {
                fixedCategory = '取得資格';
              } else if (category === '語\n学') {
                fixedCategory = '語学';
              } else if (category === 'ヒ\nュ\n｜\nマ\nン\nス\nキ\nル') {
                fixedCategory = 'ヒューマンスキル';
              }

              if (fixedCategory === '語学') {
                skillSheetsData.push({
                  category: fixedCategory,
                  name: fixedCategory,
                  skillLevel: name,
                  });
                } else {
                  skillSheetsData.push({
                  category: fixedCategory,
                  name,
                  skillLevel,
                  });
                }
          }
        }

      const authUserId = localStorage.getItem("authUserId")
      const sessionKey = localStorage.getItem("sessionKey")

      const result = await skillSheetApi.editSkillSheet(authUserId, sessionKey, userName, careerData, careerItemsData, skillSheetsData)
      if(result){
        alert("正常にスキルシートファイルが登録されました。")
      console.log(`${userName}様の Excelファイル パーシング結果:\n`, result)
      }

      return result

  }

}
